import { useQuery } from '@tanstack/react-query';

import React, { Dispatch, SetStateAction, useMemo, useState } from 'react';

import { Chip, Typography } from '@mui/material';

import { useApiClient } from '@@common';

import { ContainerImageModel, EnvironmentClient, SearchContainerImageRequest } from '../../../api/api';
import SidePanelList, { SidePanelListColumn } from '../../../components/Panels/SidePanelList';

interface ContainerImagesPanelProps {
	onClose: () => void;
	onImageSelect: (imageId: string) => void;
	selectedImageId?: string | null;
	refreshTrigger?: number; // External trigger to refresh the data
	onCollapseChange?: (collapsed: boolean) => void;
	width: number;
	setWidth: Dispatch<SetStateAction<number>>;
	top?: number;
}

export const CONTAINER_IMAGES_PANEL_STORAGE_KEY = 'containerImagesPanel.width';
export const CONTAINER_IMAGES_PANEL_DEFAULT_WIDTH = 500;
export const CONTAINER_IMAGES_PANEL_MIN_WIDTH = 360;
export const CONTAINER_IMAGES_PANEL_MAX_WIDTH = 900;
export const CONTAINER_IMAGES_PANEL_COLLAPSED_WIDTH = 48;

export default function ContainerImagesPanel({
	onClose,
	onImageSelect,
	selectedImageId,
	refreshTrigger,
	onCollapseChange,
	width,
	setWidth,
	top = 0,
}: ContainerImagesPanelProps) {
	const client = useApiClient(EnvironmentClient) as EnvironmentClient;
	const [refreshKey, setRefreshKey] = useState(0);
	const [isCollapsed, setIsCollapsed] = useState(false);

	const handleToggleCollapse = () => {
		const newCollapsed = !isCollapsed;
		setIsCollapsed(newCollapsed);
		if (onCollapseChange) {
			onCollapseChange(newCollapsed);
		}
	};

	// Query to fetch container images
	const { data, isLoading, isFetching, error, refetch } = useQuery({
		queryKey: ['container-images', refreshKey, refreshTrigger],
		queryFn: async () => {
			const query: SearchContainerImageRequest = {
				paging: {
					pageIndex: 0,
					pageSize: 1000, // Get all container images
					sortBy: 'Name',
					sortAscending: true,
				},
			};
			return await client.search2(query);
		},
	});

	const handleRefresh = () => {
		setRefreshKey((prev) => prev + 1);
		refetch();
	};

	// Get container images array
	const containerImages = useMemo(() => data?.items || [], [data?.items]);

	// Define table columns
	const columns: SidePanelListColumn[] = [
		{ id: 'name', label: 'Name', align: 'left' },
		{ id: 'latestVersion', label: 'Latest Version', align: 'left' },
		{ id: 'versions', label: 'Versions', align: 'center' },
	];

	// Render cell content based on column
	const renderCell = (image: ContainerImageModel, columnId: string) => {
		switch (columnId) {
			case 'name':
				return (
					<Typography variant="body2" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
						{image.name}
					</Typography>
				);
			case 'latestVersion':
				return (
					<Typography variant="body2" sx={{ fontSize: '0.75rem', fontFamily: 'monospace', color: 'text.secondary' }}>
						{image.latestVersion || '-'}
					</Typography>
				);
			case 'versions':
				return <Chip label={image.versions?.length || 0} size="small" color="primary" variant="outlined" sx={{ fontSize: '0.7rem' }} />;
			default:
				return null;
		}
	};

	return (
		<SidePanelList
			title="Container Images"
			subtitle="All available container images"
			items={containerImages}
			columns={columns}
			isLoading={isLoading}
			isFetching={isFetching}
			error={error as Error | null}
			emptyMessage="No container images found"
			selectedItemId={selectedImageId}
			width={width}
			collapsedWidth={CONTAINER_IMAGES_PANEL_COLLAPSED_WIDTH}
			isCollapsed={isCollapsed}
			onClose={onClose}
			onRefresh={handleRefresh}
			onItemSelect={(image) => onImageSelect(image.id)}
			renderCell={renderCell}
			getItemId={(image) => image.id}
			onToggleCollapse={handleToggleCollapse}
			resizeStorageKey={CONTAINER_IMAGES_PANEL_STORAGE_KEY}
			defaultWidth={CONTAINER_IMAGES_PANEL_DEFAULT_WIDTH}
			minWidth={CONTAINER_IMAGES_PANEL_MIN_WIDTH}
			maxWidth={CONTAINER_IMAGES_PANEL_MAX_WIDTH}
			setWidth={setWidth}
			top={top}
		/>
	);
}
